/**
 * Shared "newest course" resolution for course pickers.
 *
 * Course IDs carry their own version suffix (``DHOST 26-005``, ``DHOQC 25-002``),
 * so the newest iteration of a program is the highest year then the highest
 * sequence number inside that year. Pattern of Life Analysis and the SENTINEL
 * data selector both badge that entry as LATEST, and they share this helper so
 * the two never disagree about which course is current.
 */

/** Programs that get a LATEST badge, matched case-insensitively by prefix. */
export const LATEST_COURSE_PREFIXES = ['DHOST', 'DHOQC'] as const;

export interface LatestCourseIds {
  dhost: string | null;
  dhoqc: string | null;
}

function parseCourseVersion(courseId: string): { year: number; number: number } | null {
  const match = courseId.match(/(\d{2})-(\d+)$/);
  if (!match) return null;
  return { year: parseInt(match[1], 10), number: parseInt(match[2], 10) };
}

function findMostRecent(courseIds: string[], prefix: string): string | null {
  const matching = courseIds.filter((id) => id.toUpperCase().startsWith(prefix));
  if (matching.length === 0) return null;

  let best = matching[0];
  let bestVersion = parseCourseVersion(best);

  for (const id of matching) {
    const version = parseCourseVersion(id);
    if (!version || !bestVersion) continue;
    if (
      version.year > bestVersion.year ||
      (version.year === bestVersion.year && version.number > bestVersion.number)
    ) {
      best = id;
      bestVersion = version;
    }
  }

  return best || null;
}

/** Resolve the newest DHOST and DHOQC course IDs from a list of course IDs. */
export function resolveLatestCourses(courseIds: string[]): LatestCourseIds {
  return {
    dhost: findMostRecent(courseIds, 'DHOST'),
    dhoqc: findMostRecent(courseIds, 'DHOQC'),
  };
}

/** True when `courseId` is the newest entry for its program. */
export function isLatestCourse(courseId: string, latest: LatestCourseIds): boolean {
  return Boolean(courseId) && (courseId === latest.dhost || courseId === latest.dhoqc);
}
